import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from '@mantine/form';
import { TextInput, Button, Group, Box, Tabs, ActionIcon, TagsInput, MultiSelect } from '@mantine/core';
import HttpClient from '../helpers/HttpClient';
import { toDateOnlyString } from '../helpers/dateOnly';
import { useNavigate } from 'react-router-dom';
import { notify } from '../components/NotificationUtils';
import { ContractListForPerson } from '../components/ContractList';
import { PersonMatchList } from '../components/PersonMatchList';
import { Person } from '../types/person.type';
import { IconPhoto, IconBook, IconContract, IconFreeRights, IconPhone, IconMail, IconBallFootball } from '@tabler/icons-react';
import { TAccount } from '../components/TAccount';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { DateInput } from '@mantine/dates';
import UserRightsEdit from './UserRightsEdit';
import { useUserContext } from '../providers/UserContext';
import { useLanguage } from '../providers/LanguageContext';
import { USER_RIGHTS } from '../constants/userRights';
import { Tag } from '../types/tag.type';
import { Player } from '../types/player.type';

const PersonEdit: React.FC<{ id: number | undefined }> = ({ id }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [isUpdate, setIsUpdate] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState(false);  
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [selectedTagNames, setSelectedTagNames] = useState<string[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<number[]>([]);
  const [personOptions, setPersonOptions] = useState<Person[]>([]);
  const [selectedParentIds, setSelectedParentIds] = useState<number[]>([]);

  const { userRights } = useUserContext();
  const { t } = useLanguage();
  const pe = t.personEdit;
  const c = t.common;
  const navigate = useNavigate();

  const httpClient = new HttpClient<Person>('/person');

  const form = useForm<Person>({
    initialValues: {
      id: 0,
      firstName: '',
      lastName: '',
      birthDate: undefined,
      street: '',
      city: '',
      zip: '',
      email: '',
      phone1: '',
      emergencyPhone: '',
      emergencyContact: '',
      iban: '',
      socialInsuranceNumber: '',
      notes: '',
    },
    validate: {
      firstName: (value) => (value ? null : pe.validFirstName),
      lastName: (value) => (value ? null : pe.validLastName),
      email: (value) =>
        value && !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value)
          ? pe.validEmail
          : null,
    },
  });
 
  useEffect(() => {
    const fetchTags = async () => {
      const httpClient = new HttpClient<Tag>('/tag');
      const tags = await httpClient.getAll();
      setAllTags([...tags].sort((a, b) => a.name.localeCompare(b.name)));
    };
    const fetchPlayers = async () => {
      const httpClient = new HttpClient<Player>('/player');
      const data = await httpClient.getAll();
      setPlayers(data);
    };
    const fetchPersonOptions = async () => {
      const httpClient = new HttpClient<Person>('/person');
      const data = await httpClient.getAll({ slim: 'true' });
      setPersonOptions(data);
    };
    const fetchData = async () => {
      setLoading(true);
      try {
        if (id) {
          const result: any = await httpClient.getById(id);
          const person = result.data as Person;
          form.setValues(person);
          setIsUpdate(true);

          if (person.tags) {
            setSelectedTagNames(person.tags.map((tag: Tag) => tag.name));
            setSelectedTagIds(person.tags.map((tag: Tag) => tag.id));
          }
          if (person.players) {
            setSelectedPlayerIds(person.players.map((p: Player) => p.id));
          }
          setSelectedParentIds((person.parents ?? []).map((p: Person) => p.id));
        }
      } catch (error) {
        console.error('Failed to fetch data', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTags();
    fetchPlayers();
    fetchPersonOptions();
    fetchData();
  }, []);

  const tagNames = useMemo(() => allTags.map(tag => tag.name), [allTags]);

  if (loading) {
    return <div>{c.loading}</div>;
  }

  // Always save: parent, tags, players and birth date are held outside the
  // Mantine form fields, so form.isTouched() missed changes to them.
  const onSubmit = async (values: Person) => {
    try {
      console.log('Submitting person with tags:', values);

      // Build tags array from selected tag IDs
      const selectedTags = allTags.filter(tag => selectedTagIds.includes(tag.id));
      // players is the inverse side of a Player -> Person relation and isn't
      // writable through the generic upsert; it's synced separately below.
      const { players, ...personValues } = form.values;
      void players;
      const payload: any = {
        ...personValues,
        tags: selectedTags,
        parents: selectedParentIds.map((pid) => ({ id: pid })),
        // Sent as a plain date-only string, not a Date — see toDateOnlyString.
        birthDate: personValues.birthDate ? toDateOnlyString(new Date(personValues.birthDate)) : undefined,
      };
      console.log('Payload to submit:', payload);
      const response = await httpClient.post(payload);
      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Failed to submit form');
      }

      await new HttpClient('/person').postAction(`${response.data.id}/players`, { playerIds: selectedPlayerIds });

      notify('Success', `Entity ${response.data.id} ${isUpdate ? 'updated' : 'created'} successfully`, 'green');
      navigate(-1);
    } catch (error) {
      notify('Error', 'Entity could not be updated/saved! ' + error, 'red' );
    }
  };

  const onDelete = async () => {
    try {
      if (!id) {
        throw new Error('Failed to delete Entity without id');
      }

      const response = await httpClient.delete(id);
      
      if (response.status !== 200) {
        throw new Error('Failed to delete Entity');
      }

      notify('Success', `Entity ${id} deleted successfully`, 'green');
      navigate(-1);
    } catch (error) {
      notify('Error', error as string, 'red');
    }
  };

  return (
    <>
      <ConfirmDialog
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={onDelete}
        title={pe.confirmDeleteTitle}
        message={pe.confirmDeleteMsg}
        confirmLabel={c.delete}
        cancelLabel={c.cancel}
      />
      <Tabs defaultValue="basic">
        <Tabs.List>
          <Tabs.Tab value="basic" leftSection={<IconPhoto size={12} />}>
            {pe.tabBasic}
          </Tabs.Tab>
          {(userRights.includes(USER_RIGHTS.CONTRACTS) || userRights.includes(USER_RIGHTS.ADMIN)) && (
          <Tabs.Tab value="contracts" leftSection={<IconContract size={12} />}>
            {pe.tabContracts}
          </Tabs.Tab>
          )}
          {selectedPlayerIds.length > 0 && (
            <Tabs.Tab value="matches" leftSection={<IconBallFootball size={12} />}>
              {pe.tabMatches}
            </Tabs.Tab>
          )}
          {(userRights.includes(USER_RIGHTS.ACCOUNTING) || userRights.includes(USER_RIGHTS.ADMIN)) && (
            <Tabs.Tab value="accounting" leftSection={<IconBook size={12} />}>
              {pe.tabAccount}
            </Tabs.Tab>
          )}
          {userRights.includes(USER_RIGHTS.ADMIN) && (
            <Tabs.Tab value="rights" leftSection={<IconFreeRights size={12} />}>
              {pe.tabRights}
            </Tabs.Tab>
          )}
        </Tabs.List>    

        <Tabs.Panel value="basic" pt="xs">
        <Box mx="auto">
          <form onSubmit={form.onSubmit((values) => onSubmit(values))}>
              <TextInput label={pe.firstName} {...form.getInputProps('firstName')} />
              <TextInput label={pe.lastName} {...form.getInputProps('lastName')} />
              <DateInput
                clearable
                valueFormat="DD.MM.YYYY"
                label={pe.birthDate}
                value={form.values.birthDate ? new Date(form.values.birthDate) : null}
                onChange={date => form.setFieldValue('birthDate', date ? new Date(date) : undefined )}
                dateParser={value => {
                  // Parse DD.MM.YYYY to Date
                  const parts = value.split('.');
                  if (parts.length === 3) {
                    const day = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const year = parseInt(parts[2], 10);
                    if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
                      return new Date(year, month, day);
                    }
                  }
                  return new Date(value);
                }}                
              />
              <TextInput label={pe.birthPlace} {...form.getInputProps('birthPlace')} />
              <TextInput label={pe.nationality} {...form.getInputProps('nationality')} />

              <TextInput label={pe.street} {...form.getInputProps('street')} />
              <TextInput label={pe.city} {...form.getInputProps('city')} />
              <TextInput label={pe.zip} {...form.getInputProps('zip')} />
              <TextInput
                label={pe.email}
                {...form.getInputProps('email')}
                leftSection={
                  form.values.email ? (
                    <ActionIcon
                      variant="subtle"
                      color="blue"
                      size="sm"
                      onClick={() => window.open(`mailto:${form.values.email}`, '_self')}
                      aria-label="Send email"
                    >
                      <IconMail size={18} />
                    </ActionIcon>
                  ) : null
                }
              />
              <TextInput
                label={pe.phone}
                {...form.getInputProps('phone1')}
                leftSection={
                  form.values.emergencyPhone ? (
                    <ActionIcon
                      variant="subtle"
                      color="blue"
                      size="sm"
                      onClick={() => window.open(`tel:${form.values.phone1}`, '_self')}
                      aria-label="Call phone"
                    >
                      <IconPhone size={18} />
                    </ActionIcon>
                  ) : null
                }
              />              
              <TextInput
                label={pe.emergencyPhone}
                {...form.getInputProps('emergencyPhone')}
                leftSection={
                  form.values.emergencyPhone ? (
                    <ActionIcon
                      variant="subtle"
                      color="blue"
                      size="sm"
                      onClick={() => window.open(`tel:${form.values.emergencyPhone}`, '_self')}
                      aria-label="Call emergency phone"
                    >
                      <IconPhone size={18} />
                    </ActionIcon>
                  ) : null
                }
              />
              <TextInput label={pe.emergencyContact} {...form.getInputProps('emergencyContact')} />
              <TextInput label={pe.iban} {...form.getInputProps('iban')} />
              <TextInput label={pe.sv} {...form.getInputProps('socialInsuranceNumber')} />
              <TextInput label={pe.notes} {...form.getInputProps('notes')} />
              <TextInput label={pe.oid} value={form.values.oid} disabled />
              <MultiSelect
                label={pe.parent}
                placeholder={pe.parentSearch}
                searchable
                clearable
                data={personOptions
                  .filter(p => p.id !== form.values.id)
                  .map(p => ({ value: String(p.id), label: `${p.firstName} ${p.lastName}` }))}
                value={selectedParentIds.map(String)}
                onChange={vals => setSelectedParentIds(vals.map(Number))}
              />
              <MultiSelect
                label={pe.oefbPlayer}
                placeholder={pe.playerSearch}
                searchable
                clearable
                data={players.map(p => ({ value: String(p.id), label: `${p.name}${p.position ? ` (${p.position})` : ''}` }))}
                value={selectedPlayerIds.map(String)}
                onChange={vals => setSelectedPlayerIds(vals.map(Number))}
              />
              <TagsInput
                label={pe.tags}
                data={tagNames}
                value={selectedTagNames}
                onChange={names => {
                  setSelectedTagNames(names);
                  const selectedTags = allTags.filter(tag => names.includes(tag.name));
                  setSelectedTagIds(selectedTags.map(tag => tag.id));
                }}
                clearable
              />
            <Group mt="md">
              <Button type="submit">{c.save}</Button>
              {isUpdate && <Button type="button" color="red" onClick={ ()=>setConfirmOpen(true) }>{c.delete}</Button>}

            </Group>
          </form>
        </Box>
        </Tabs.Panel>
        <Tabs.Panel value="contracts" pt="xs">
        {isUpdate && form.values.id && (
          <ContractListForPerson personId={form.values.id} />
        )}
        </Tabs.Panel>
        <Tabs.Panel value="matches" pt="xs">
          {isUpdate && form.values.id && (
            <PersonMatchList personId={form.values.id} />
          )}
        </Tabs.Panel>
        <Tabs.Panel value="accounting" pt="xs">
        {isUpdate && form.values.id && (
          <TAccount personId={form.values.id} />
        )}
        </Tabs.Panel>
        <Tabs.Panel value="rights" pt="xs">
        {isUpdate && form.values.id && (
          <UserRightsEdit personId={form.values.id}/>
        )}
        </Tabs.Panel>
    </Tabs>
  </>
)};

export default PersonEdit;
